import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { graphQueries } from '../../api/graph.api';
import { AppSettingsDto, AppPolicyBundle } from '../../api/app.types';
import { AppBaseDto } from '../../api/graph.types';
import { isCountableOwner } from '../owners';
import useSecondaryOwners from './useSecondaryOwners';

export type OwnerKindCompliance = {
  /** The policy's minimum; `undefined` when no policy sets one. */
  minimum?: number;
  /** Owners that count towards the minimum; `undefined` while they are being resolved or could not be. */
  count?: number;
  /** Fewer countable owners than the policy requires. Never true while the count is unknown. */
  isBelowMinimum: boolean;
  /** Removing a countable owner would breach the policy. Never true while the count is unknown; the server still enforces it. */
  isAtMinimum: boolean;
  /** Whether removing this owner counts against the minimum. Removing one that never counted is always allowed. */
  isCountable: (ownerId: string | null | undefined) => boolean;
  /**
   * The signed-in user is an application management administrator, who can do whatever they want with an app's
   * owners: the server lets them past the minimum, so nothing is held back for them. The counts and warnings stay.
   */
  canRemoveBelowMinimum: boolean;
};

export type OwnerCompliance = {
  application: OwnerKindCompliance;
  technical: OwnerKindCompliance;
  business: OwnerKindCompliance;
};

const toKindCompliance = (
  minimum: number | undefined,
  countableIds: string[] | undefined,
  canRemoveBelowMinimum: boolean
): OwnerKindCompliance => {
  const count = countableIds?.length;

  return {
    minimum,
    count,
    isBelowMinimum: minimum !== undefined && count !== undefined && count < minimum,
    isAtMinimum: minimum !== undefined && count !== undefined && count <= minimum,
    isCountable: (ownerId) => !!ownerId && !!countableIds?.includes(ownerId),
    canRemoveBelowMinimum,
  };
};

/**
 * The ids among `ownerIds` that resolve to a user, or `undefined` while that is
 * not known. A lookup that failed leaves the answer unknown rather than "not countable", so a transient
 * Graph error cannot show a breach that is not there. Nothing is looked up when `enabled` is false.
 */
const useCountableSecondaryOwners = (ownerIds: string[], enabled: boolean): string[] | undefined => {
  const { users, isSettled } = useSecondaryOwners(ownerIds, enabled);

  return useMemo(
    () => (enabled && isSettled ? users.flatMap((user) => (isCountableOwner(user) ? [user.id!] : [])) : undefined),
    [enabled, isSettled, users]
  );
};

/**
 * Counts owners the way the backend does (`IOwnerCounter`), because the removal guard and the compliance
 * scan both use that count: every application owner except the EasyLife 365 Identity service principal; technical
 * and business owners while they still resolve to a user. A deleted user is stored as an owner but does not count,
 * so the stored length is not the count.
 */
const useOwnerCompliance = (
  app: AppBaseDto,
  settings: AppSettingsDto,
  policyBundle: AppPolicyBundle | null
): OwnerCompliance => {
  // Until the answer is in, the user is held to the minimum: it is only lifted for someone known to be an admin.
  const { data: adminInfo } = useQuery({ ...graphQueries.isAppMgmtAdmin, staleTime: 5 * 60 * 1000 });
  const isAdmin = adminInfo?.isAdmin === true;

  const minTechnical = policyBundle?.minTechnicalOwnersPolicy?.minTechnicalOwners;
  const minBusiness = policyBundle?.minBusinessOwnersPolicy?.minBusinessOwners;

  const technical = useCountableSecondaryOwners(
    settings.technicalOwnershipSettings.owners.map((owner) => owner.id),
    minTechnical !== undefined
  );
  const business = useCountableSecondaryOwners(
    settings.businessOwnershipSettings.owners.map((owner) => owner.id),
    minBusiness !== undefined
  );

  const applicationOwners = app.owners;
  const minApplication = policyBundle?.minOwnersPolicy?.minOwners;

  // Memoised so the compliance objects keep their identity, which the owner tables list as a dependency.
  const application = useMemo(
    () =>
      toKindCompliance(
        minApplication,
        applicationOwners.filter((owner) => owner.id && isCountableOwner(owner)).map((owner) => owner.id!),
        isAdmin
      ),
    [minApplication, applicationOwners, isAdmin]
  );
  const technicalCompliance = useMemo(
    () => toKindCompliance(minTechnical, technical, isAdmin),
    [minTechnical, technical, isAdmin]
  );
  const businessCompliance = useMemo(
    () => toKindCompliance(minBusiness, business, isAdmin),
    [minBusiness, business, isAdmin]
  );

  return { application, technical: technicalCompliance, business: businessCompliance };
};

export default useOwnerCompliance;
